import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Booking, Room } from '../../api/rooms';
import {
  GRID_HEIGHT,
  instantFor,
  minutesOfInstant,
  minutesToY,
  packLanes,
  timeLabel,
} from '../../lib/calendar';
import { Icon } from '../../lib/icons';
import { NowLine } from './NowLine';
import { TimeGrid } from './TimeGrid';
import { useDragToCreate } from './useDragToCreate';

export interface DayGridProps {
  date: string;
  rooms: Room[];
  bookings: Booking[];
  onOpen: (booking: Booking) => void;
  /** Drag finished — room, date and time are all fixed (REQ-037). */
  onDraft: (draft: { roomId: string; fromMinute: number; toMinute: number }) => void;
}

/**
 * The day view: one column per bookable room.
 *
 * A drag here fixes all three of room, date and time, and the dialog shows them
 * read-only — the column *is* the room, so offering to change it afterwards
 * would contradict what the user just did.
 *
 * Rooms the caller may not book never appear (REQ-043), which is also why an
 * empty grid is a real state worth its own message rather than a blank frame.
 */
export function DayGrid({ date, rooms, bookings, onOpen, onDraft }: DayGridProps): ReactElement {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const { selection, handlers } = useDragToCreate((s) =>
    onDraft({ roomId: s.key, fromMinute: s.fromMinute, toMinute: s.toMinute }),
  );

  const byRoom = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const booking of bookings) {
      if (booking.date !== date) continue;
      const list = map.get(booking.room_id) ?? [];
      list.push(booking);
      map.set(booking.room_id, list);
    }
    return map;
  }, [bookings, date]);

  if (rooms.length === 0) {
    return (
      <div className="empty">
        <div className="em-ic">
          <Icon name="calendar" />
        </div>
        {t('rm_none_bookable')}
      </div>
    );
  }

  return (
    <TimeGrid
      heads={rooms.map((room) => (
        <div key={room.id} className="cal-room-head">
          {room.name}
          <span className="cap">
            {t('bk_cap')} {room.capacity}
          </span>
        </div>
      ))}
      columns={rooms.map((room) => {
        // Two meetings cannot overlap in one room — the exclusion constraint
        // makes that impossible — so lane packing here only ever yields one
        // lane. It runs anyway so the day and week views stay the same code.
        const placed = packLanes(
          byRoom.get(room.id) ?? [],
          (b) => minutesOfInstant(b.starts_at),
          (b) => minutesOfInstant(b.ends_at),
        );

        return (
          <div
            key={room.id}
            className="cal-room-col"
            style={{ height: GRID_HEIGHT, touchAction: 'none' }}
            {...handlers(room.id)}
          >
            <NowLine day={date} />

            {placed.map(({ item, lane, lanes }) => {
              const top = minutesToY(minutesOfInstant(item.starts_at));
              const height = Math.max(20, minutesToY(minutesOfInstant(item.ends_at)) - top);
              const width = 100 / lanes;

              return (
                <button
                  key={item.id}
                  type="button"
                  className="cal-event"
                  title={item.title}
                  onClick={() => onOpen(item)}
                  style={{
                    top,
                    height,
                    left: `calc(${lane * width}% + 2px)`,
                    width: `calc(${width}% - 4px)`,
                    insetInlineEnd: 'auto',
                  }}
                >
                  <b>{item.title}</b>
                  {timeLabel(item.starts_at, locale)}–{timeLabel(item.ends_at, locale)}
                </button>
              );
            })}

            {selection?.key === room.id ? (
              <div
                className="cal-drag"
                style={{
                  top: minutesToY(selection.fromMinute),
                  height: Math.max(
                    20,
                    minutesToY(selection.toMinute) - minutesToY(selection.fromMinute),
                  ),
                }}
              >
                {timeLabel(instantFor(date, selection.fromMinute), locale)} –{' '}
                {timeLabel(instantFor(date, selection.toMinute), locale)}
              </div>
            ) : null}
          </div>
        );
      })}
    />
  );
}
